<template>
  <div class="companies">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs
          :items="[
            { text: $t('containerGroups.containerGroups'), name: 'company_container_groups' },
            { text: containerGroup.name },
          ]"
        />
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <LoadingSpinner v-if="loading" full />
        <Fragment v-else>
          <div class="content-wrap">
            <Form :bodyClass="''" :title="$t('meta.baseData')" @submit="onFormSubmitted">
              <FormInput v-model="containerGroup.name" :name="$t('meta.name')" type="text" required />
              <GeoCoder :latitude="containerGroup.lat" :longitude="containerGroup.lng" @coordinates="getCoordinates" />
              <Button type="submit" fullWidth>{{ $t('meta.save') }}</Button>
            </Form>
          </div>

          <div class="content-wrap">
            <header class="content-header cf">
              <h3>{{ $t('meta.containers') }}</h3>
            </header>
            <div class="content-body">
              <div v-if="!containerGroup" class="alert alert-danger">{{ $t('containerGroups.noContainers') }}</div>
              <Table v-else>
                <template #header>
                  <th>{{ $t('containerGroups.name') }}</th>
                  <th>{{ $t('containerGroups.fraction') }}</th>
                  <th>
                    {{ $t('containerGroups.emptyTemplates') }}
                  </th>
                  <th>
                    <span class="visually-hidden">{{ $t('meta.actions') }}</span>
                  </th>
                </template>
                <template #body>
                  <tr v-for="container in containerGroup.containers" :key="container.uuid">
                    <td>{{ container.name }}</td>
                    <td>{{ container.fraction }}</td>
                    <td>
                      <span v-if="container.empty_templates.length == 0">&mdash;</span>
                      <span v-else v-for="empty_template in container.empty_templates" :key="empty_template.uuid">
                        <EmptyingTemplateDescription
                          :repeatType="empty_template.repeat_type"
                          :repeatInterval="empty_template.repeat_interval"
                          :referenceDate="empty_template.reference_date"
                        />
                      </span>
                    </td>
                    <td class="table__actions">
                      <Link :to="`/company/containers/${container.uuid}`">
                        <span class="material-icons">edit</span>
                      </Link>
                    </td>
                  </tr>
                </template>
              </Table>
            </div>
          </div>

          <div class="content-wrap">
            <header class="content-header cf">
              <h3>{{ $t('containers.emptyTemplates') }}</h3>
              <div class="header-meta">
                <Link
                  :to="`/company/container_groups/${$route.params.container_group_uuid}/empty_templates/new`"
                  class="button button-green button-small button-round"
                  >{{ $t('containers.addEmptyingTemplate') }}</Link
                >
              </div>
            </header>
            <div class="content-body">
              <Table v-if="containerGroup && containerGroup.common_empty_templates.length > 0">
                <template #header>
                  <th>{{ $t('containers.emptyingDates') }}</th>
                  <th>
                    {{ $t('containers.emptyingDatePosition') }}
                  </th>
                  <th>
                    <span class="visually-hidden">{{ $t('meta.actions') }}</span>
                  </th>
                </template>
                <template #body>
                  <tr v-for="emptyTemplate in containerGroup.common_empty_templates" :key="emptyTemplate.uuid">
                    <td>
                      <EmptyingTemplateDescription
                        :repeatType="emptyTemplate.repeat_type"
                        :repeatInterval="emptyTemplate.repeat_interval"
                        :referenceDate="emptyTemplate.reference_date"
                      />
                    </td>
                    <td>{{ emptyTemplate.route_position }}</td>
                    <td class="table__actions">
                      <Link
                        :to="`/company/container_groups/${containerGroup.uuid}/empty_templates/${emptyTemplate.uuid}`"
                        ><span class="material-icons">edit</span></Link
                      >
                      <button class="table__action-button" @click="deleteEmptyTemplate(emptyTemplate.uuid)">
                        <span class="material-icons">delete</span>
                      </button>
                    </td>
                  </tr>
                </template>
              </Table>
              <div v-else class="alert alert-info">{{ $t('containers.noEmptyTemplates') }}</div>
            </div>
          </div>
        </Fragment>
      </div>
    </div>
  </div>
</template>

<script>
import { Fragment } from 'vue-fragment'

import BreadCrumbs from '@/components/BreadCrumbs'
import Button from '@/components/Button'
import EmptyingTemplateDescription from '@/components/EmptyingTemplates/Description.vue'
import FormInput from '@/components/Form/Input/index.vue'
import Form from '@/components/Form/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table/index.vue'
import CompanyNav from '@/components/company_nav'
import GeoCoder from '@/components/geocoder'

import containerGroupsApi from '@/api/containerGroups'
import emptyTemplatesApi from '@/api/emptyTemplates'

export default {
  name: 'company_container_group',
  inject: ['companyUser', 'showPopup'],
  data() {
    return {
      containerGroup: {},
      errors: [],
      current_markers: [],
      loading: true,
    }
  },
  components: {
    BreadCrumbs,
    Button,
    CompanyNav,
    Form,
    FormInput,
    Fragment,
    EmptyingTemplateDescription,
    GeoCoder,
    LoadingSpinner,
    SectionHeader,
    Table,
  },
  methods: {
    getCoordinates(value) {
      this.containerGroup.lat = value[1]
      this.containerGroup.lng = value[0]
    },
    async onFormSubmitted() {
      await this.updateContainerGroup()
    },
    async getData() {
      await this.getContainerGroup()
      this.loading = false
    },
    async getContainerGroup() {
      this.loading = true
      const result = await containerGroupsApi.get(this.$route.params.container_group_uuid)
      if (result.success) {
        this.containerGroup = result.containerGroup
      }
    },
    async updateContainerGroup() {
      const result = await containerGroupsApi.update(this.containerGroup.uuid, this.containerGroup)
      if (result.success) {
        this.containerGroup = result.containerGroup
      }
    },
    async deleteEmptyTemplate(emptyTemplateUuid) {
      const popupResult = await this.showPopup({
        content: this.$t('emptyTemplatesForms.deleteConfirmation'),
        type: 'confirm',
      })
      if (!popupResult) return

      const result = await emptyTemplatesApi.deleteFromGroup(this.$route.params.container_group_uuid, emptyTemplateUuid)
      if (result.success) {
        await this.getData()
      }
    },
  },
  async mounted() {
    await this.getData()
  },
}
</script>
